<p-confirmDialog [closable]="false" [key]="dialogKey" [baseZIndex]="1000"></p-confirmDialog>
<div *ngIf="isPageReady" class="app-bulk-image-upload flex h-full flex-col">
  <!-- Topbar -->
  <div class="app-bulk-image-upload__topbar shrink-0">
    <!-- Three labeled card sections -->
    <div class="app-bulk-image-upload__sections">
      <!-- SOURCE -->
      <section class="app-bulk-image-upload__card">
        <div class="app-bulk-image-upload__card-title">Source</div>
        <div class="app-bulk-image-upload__card-body">
          <div class="app-bulk-image-upload__field">
            <label class="app-bulk-image-upload__field-label">Manufacturer</label>
            <p-select
              [options]="manufacturers"
              [(ngModel)]="selectedManufacturer"
              optionLabel="name"
              placeholder="All Manufacturers"
              [showClear]="true"
              (onChange)="onManufacturerChanged()"
              [style]="{ width: '100%', 'min-width': '220px' }"
            ></p-select>
          </div>
          <div class="mt-3">
            <p-button
              size="small"
              icon="pi pi-upload"
              label="Select Images"
              (click)="selectFiles()"
              styleClass="app-bulk-image-upload__primary-btn"
            ></p-button>
          </div>
          <input
            #filePicker
            type="file"
            accept="image/png,image/jpeg,image/webp,.png,.jpeg,.jpg,.webp"
            multiple
            (change)="onFilesSelected($event)"
            style="display: none"
          />
        </div>
      </section>

      <!-- OPTIONS -->
      <section class="app-bulk-image-upload__card">
        <div class="app-bulk-image-upload__card-title">Options</div>
        <div class="app-bulk-image-upload__card-body app-bulk-image-upload__card-body--stack">
          <div class="app-bulk-image-upload__group">
            <p-checkbox [(ngModel)]="setAsPrimary" inputId="setAsPrimary" [binary]="true"></p-checkbox>
            <label for="setAsPrimary" class="cursor-pointer text-sm">Set as Primary</label>
          </div>
          <div class="app-bulk-image-upload__group">
            <p-checkbox [(ngModel)]="skipExisting" inputId="skipExisting" [binary]="true"></p-checkbox>
            <label for="skipExisting" class="cursor-pointer text-sm">Skip if already has images</label>
          </div>
        </div>
      </section>

      <!-- ACTIONS -->
      <section class="app-bulk-image-upload__card">
        <div class="app-bulk-image-upload__card-title">Actions</div>
        <div class="app-bulk-image-upload__card-body app-bulk-image-upload__card-body--wrap">
          <p-button
            size="small"
            icon="pi pi-times"
            severity="secondary"
            label="Remove Unmatched"
            [disabled]="unmatchedCount === 0"
            (click)="removeUnmatched()"
          ></p-button>
          <p-button
            size="small"
            icon="pi pi-trash"
            severity="danger"
            label="Clear All"
            [disabled]="totalCount === 0"
            (click)="clearAll()"
          ></p-button>
        </div>
      </section>
    </div>

    <!-- Status strip -->
    <div class="app-bulk-image-upload__status">
      <button
        type="button"
        class="app-bulk-image-upload__help-btn"
        (click)="showHelp = !showHelp"
        [attr.aria-expanded]="showHelp"
        [title]="showHelp ? 'Hide naming instructions' : 'Show naming instructions'"
      >
        <i class="pi pi-info-circle"></i>
        <span>Naming Rules</span>
        <i class="pi" [class.pi-chevron-down]="!showHelp" [class.pi-chevron-up]="showHelp"></i>
      </button>

      <div class="app-bulk-image-upload__stats">
        <span class="app-bulk-image-upload__stat">
          <span class="app-bulk-image-upload__stat-label">Total</span>
          <span class="app-bulk-image-upload__stat-value">{{ totalCount }}</span>
        </span>
        <span class="app-bulk-image-upload__stat app-bulk-image-upload__stat--matched">
          <span class="app-bulk-image-upload__stat-label">Matched</span>
          <span class="app-bulk-image-upload__stat-value">{{ matchedCount }}</span>
        </span>
        <span class="app-bulk-image-upload__stat app-bulk-image-upload__stat--unmatched">
          <span class="app-bulk-image-upload__stat-label">Unmatched</span>
          <span class="app-bulk-image-upload__stat-value">{{ unmatchedCount }}</span>
        </span>
        <span class="app-bulk-image-upload__stat app-bulk-image-upload__stat--skipped">
          <span class="app-bulk-image-upload__stat-label">Skipped</span>
          <span class="app-bulk-image-upload__stat-value">{{ skippedCount }}</span>
        </span>
      </div>

      <span *ngIf="processingStatus" class="app-bulk-image-upload__processing">
        <i class="pi pi-spin pi-spinner"></i> {{ processingStatus }}
      </span>
    </div>

    <!-- Naming Rules panel (collapsible) -->
    <div *ngIf="showHelp" class="app-bulk-image-upload__help">
      <div class="app-bulk-image-upload__help-title"><i class="pi pi-info-circle"></i> Image Filename Rules</div>
      <p class="app-bulk-image-upload__help-intro">
        Images are matched to Product Types by extracting the <strong>SKU (Style)</strong> from the filename. The filename (without its
        extension) must either equal the SKU exactly, or begin with the SKU followed by <code>-</code> or <code>_</code>. Matching is
        case-insensitive.
      </p>
      <ul class="app-bulk-image-upload__help-rules">
        <li>
          <strong>Exact match:</strong> <code>SKU.jpg</code> &nbsp;&rarr;&nbsp; matches the Product Type whose SKU is <code>SKU</code>
        </li>
        <li>
          <strong>Prefix match:</strong> <code>SKU-front.jpg</code> or <code>SKU_2.png</code> &nbsp;&rarr;&nbsp; also matches
          <code>SKU</code>
        </li>
        <li><strong>Supported formats:</strong> <code>.jpg</code>, <code>.jpeg</code>, <code>.png</code>, <code>.webp</code></li>
        <li>
          <strong>Ambiguous SKUs:</strong> if the same SKU exists on multiple manufacturers, select the manufacturer above to disambiguate
          &mdash; otherwise the image will be left unmatched
        </li>
        <li><strong>No match:</strong> use the <em>Assign</em> button on a row to pick the Product Type manually</li>
      </ul>
      <div class="app-bulk-image-upload__help-examples">
        <div class="app-bulk-image-upload__help-examples-title">Examples (for SKU <code>5170</code>):</div>
        <div class="app-bulk-image-upload__help-examples-grid">
          <span class="app-bulk-image-upload__help-good"><i class="pi pi-check"></i> 5170.jpg</span>
          <span class="app-bulk-image-upload__help-good"><i class="pi pi-check"></i> 5170-front.png</span>
          <span class="app-bulk-image-upload__help-good"><i class="pi pi-check"></i> 5170_back.webp</span>
          <span class="app-bulk-image-upload__help-bad"><i class="pi pi-times"></i> 5170front.jpg</span>
          <span class="app-bulk-image-upload__help-bad"><i class="pi pi-times"></i> shirt-5170.jpg</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Grid -->
  <app-ag-grid [gridOptions]="gridOptions" [rowData]="matchResults" class="min-h-0 flex-1"></app-ag-grid>

  <!-- Footer -->
  <div class="flex shrink-0 justify-between border-t p-3">
    <p-button label="Close" severity="secondary" (click)="onClose()"></p-button>
    <p-button icon="pi pi-save" label="Upload All Matched" [disabled]="matchedCount === 0" (click)="saveAll()"></p-button>
  </div>
</div>
